/**
 * Google Maps deep-link builders for crew navigation.
 *
 * A "stop" prefers precise coordinates and falls back to a text address, so a
 * warehouse or custom pickup with lat/lng routes exactly, while a sparse
 * address still resolves by text. Used for single-destination links and for the
 * multi-stop pickup → install journey.
 */
export interface MapStop {
  line1?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  latitude: number | null;
  longitude: number | null;
}

/** The `destination`/`waypoint` param for a stop — coords if present, else text. '' if neither. */
export function mapStopParam(p: MapStop): string {
  if (p.latitude != null && p.longitude != null) return `${p.latitude},${p.longitude}`;
  return [p.line1, p.city, p.state, p.postal_code].filter(Boolean).join(', ');
}

/** Deep link to a single destination (turn-by-turn from the device's location). */
export function destinationUrl(p: MapStop): string {
  const dest = mapStopParam(p);
  return dest
    ? `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(dest)}`
    : '';
}

/**
 * Multi-stop journey: origin → waypoints → destination, in array order.
 * Returns '' if fewer than two stops resolve to something mappable.
 */
export function journeyUrl(stops: MapStop[]): string {
  const pts = stops.map(mapStopParam).filter(Boolean);
  if (pts.length < 2) return '';
  const origin = pts[0]!;
  const destination = pts[pts.length - 1]!;
  const waypoints = pts.slice(1, -1);
  let url =
    `https://www.google.com/maps/dir/?api=1` +
    `&origin=${encodeURIComponent(origin)}` +
    `&destination=${encodeURIComponent(destination)}`;
  if (waypoints.length > 0) url += `&waypoints=${encodeURIComponent(waypoints.join('|'))}`;
  return url;
}
